import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import * as XLSX from "xlsx";

import Dialog from "../../../common/Dialog";
import { ActionType, Subaction, Ingredient } from "../../../../types/ActionType";
import { generateRandomId } from "../../../../database/hooks/generateRandomId";
import { firestore } from "../../../../database/firebase";
import DB_PATH from "../../../../database/DB_PATH";
import { createOpenAIResponse } from "../../../../services/openaiFunctions";

const AI_MODEL = "gpt-5";

const TARGET_FIELDS = [
  "id",
  "name",
  "description",
  "actionType",
  "duration",
  "repetitions",
  "sets",
  "restTime",
  "caloriesBurned",
  "intensityLevel",
  "equipmentNeeded",
  "location",
  "backgroundImageURL",
  "videoURL",
  "quantity",
  "notes",
  "reflectionPrompt",
  "instructionsURL",
  "tags",
  "ingredients",
  "createdAt",
  "updatedAt",
  "videoFileName",
  "phase",
  "primaryIntent",
  "secondaryBenefits",
  "bodyRegions",
  "movementQualities",
  "compensationsAddressed",
  "userUseCases",
  "difficultyLevel",
  "loadType",
];


type ParsedSubaction = {
  subaction: Subaction;
};

const normalizeHeader = (header: string) =>
  header.toLowerCase().replace(/[^a-z0-9]/g, "");

const headerAliases: Record<string, string> = {
  actionid: "actionId",
  actionname: "actionName",
  action: "actionName",
  parentactionid: "actionId",
  parentaction: "actionName",
  exerciseid: "id",
  exercisename: "name",
  videofilename: "videoFileName",
  protocolphaseorder: "phase",
  primaryintent: "primaryIntent",
  reflectionprompt: "reflectionPrompt",
  secondarybenefits: "secondaryBenefits",
  bodyregions: "bodyRegions",
  movementqualities: "movementQualities",
  compensationsaddressed: "compensationsAddressed",
  userusecases: "userUseCases",
  difficultylevel: "difficultyLevel",
  loadtype: "loadType",
  equipmentrequired: "equipmentNeeded",
  suggestedsets: "sets",
  suggestedrepsorhold: "repetitions",
  subactionid: "id",
  subactionname: "name",
  title: "name",
  actiontype: "actionType",
  backgroundimageurl: "backgroundImageURL",
  imageurl: "backgroundImageURL",
  videourl: "videoURL",
  instructionurl: "instructionsURL",
  instructionsurl: "instructionsURL",
  resttime: "restTime",
  caloriesburned: "caloriesBurned",
  intensitylevel: "intensityLevel",
  equipmentneeded: "equipmentNeeded",
  equipment: "equipmentNeeded",
  createdat: "createdAt",
  updatedat: "updatedAt",
};

const detectDelimiter = (line: string) => {
  const commaCount = (line.match(/,/g) || []).length;
  const semicolonCount = (line.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ";" : ",";
};

const parseCsv = (text: string, delimiter: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      row.push(current);
      current = "";
      continue;
    }

    if (!inQuotes && char === "\n") {
      row.push(current);
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
      current = "";
      continue;
    }

    if (!inQuotes && char === "\r") {
      continue;
    }

    current += char;
  }

  row.push(current);
  if (row.some((cell) => cell.trim() !== "")) rows.push(row);

  return rows;
};

const parseNumberValue = (value?: string) => {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseListValue = (value?: string) => {
  if (!value) return undefined;
  const items = value
    .split(/[|;,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
};

const parseIngredients = (value?: string): Ingredient[] | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
};

const parseActionType = (value?: string) => {
  const normalized = value?.toString().trim().toLowerCase();
  const allowedTypes = Object.values(ActionType) as string[];
  return allowedTypes.includes(normalized || "")
    ? (normalized as ActionType)
    : ActionType.CUSTOM;
};

const appendNote = (current: string | undefined, label: string, value?: string) => {
  if (!value) return current;
  const trimmed = value.trim();
  if (!trimmed) return current;
  const entry = `${label}: ${trimmed}`;
  return current ? `${current}\n${entry}` : entry;
};

const ImportSubactions = ({
  habitId,
  communityId,
  standaloneSubactions,
  setStandaloneSubactions,
}: {
  habitId: string;
  communityId?: string;
  standaloneSubactions: Subaction[];
  setStandaloneSubactions: (subactions: Subaction[]) => void;
}) => {
  const [show, setShow] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parsedSubactions, setParsedSubactions] = useState<ParsedSubaction[]>(
    []
  );
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [aiMapping, setAiMapping] = useState<Record<string, string | null>>({});
  const [isAiMapping, setIsAiMapping] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [fileRows, setFileRows] = useState<any[][]>([]);
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [defaultActionType, setDefaultActionType] = useState<ActionType>(
    ActionType.CUSTOM
  );
  const [showColumns, setShowColumns] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existingNameSet = new Set(
    (standaloneSubactions || []).map((s) => s.name.toLowerCase())
  );

  const duplicateCount = parsedSubactions.filter((item) =>
    existingNameSet.has(item.subaction.name.toLowerCase())
  ).length;


  const resetState = () => {
    setParsedSubactions([]);
    setParseErrors([]);
    setParseWarnings([]);
    setAiMapping({});
    setAiError(null);
    setFileRows([]);
    setRawHeaders([]);
    setFileName("");
  };

  const mapHeaderKey = (
    header: string,
    mapping: Record<string, string | null>
  ) => {
    if (mapping[header] === null) return "";
    if (mapping[header]) return mapping[header] as string;
    const normalizedHeader = normalizeHeader(header);
    if (mapping[normalizedHeader] === null) return "";
    if (mapping[normalizedHeader]) return mapping[normalizedHeader] as string;
    return headerAliases[normalizedHeader] || normalizedHeader;
  };

  const parseRowsToSubactions = (
    rows: any[][],
    mapping: Record<string, string | null> = {},
    fallbackActionType: ActionType = ActionType.CUSTOM
  ) => {
    const trimmedRows = rows.filter((row) =>
      row.some((cell) => String(cell ?? "").trim() !== "")
    );

    if (trimmedRows.length === 0) {
      return {
        parsed: [],
        errors: ["No rows found in file."],
        warnings: [],
      };
    }

    const rawHeaders = trimmedRows[0].map((header) =>
      header.replace(/^\uFEFF/, "").trim()
    );
    const headerKeys = rawHeaders.map((header) => {
      return mapHeaderKey(header, mapping);
    });

    const parsed: ParsedSubaction[] = [];
    const errors: string[] = [];
    const warnings: string[] = [];

    trimmedRows.slice(1).forEach((row, index) => {
      const rowIndex = index + 2;
      const rowData: Record<string, string> = {};

      headerKeys.forEach((key, cellIndex) => {
        if (!key) return;
        const cellValue = row[cellIndex];
        rowData[key] = cellValue == null ? "" : String(cellValue).trim();
      });

      const subactionName = rowData.name || rowData.actionName;
      if (!subactionName) {
        errors.push(`Row ${rowIndex}: Missing subaction name.`);
        return;
      }

      const subactionId = rowData.id || rowData.actionId || generateRandomId();

      const subaction: Subaction = {
        id: subactionId,
        name: subactionName,
        description: rowData.description || "",
        actionType: rowData.actionType
          ? parseActionType(rowData.actionType)
          : fallbackActionType,
        createdAt: rowData.createdAt || new Date().toISOString(),
        habitId,
      };

      if (rowData.phase) subaction.phase = rowData.phase;
      if (rowData.primaryIntent) subaction.primaryIntent = rowData.primaryIntent;
      if (rowData.secondaryBenefits)
        subaction.secondaryBenefits = rowData.secondaryBenefits;

      const bodyRegions = parseListValue(rowData.bodyRegions);
      if (bodyRegions) subaction.bodyRegions = bodyRegions;
      const movementQualities = parseListValue(rowData.movementQualities);
      if (movementQualities) subaction.movementQualities = movementQualities;
      const compensationsAddressed = parseListValue(
        rowData.compensationsAddressed
      );
      if (compensationsAddressed)
        subaction.compensationsAddressed = compensationsAddressed;
      const userUseCases = parseListValue(rowData.userUseCases);
      if (userUseCases) subaction.userUseCases = userUseCases;

      if (rowData.difficultyLevel)
        subaction.difficultyLevel = rowData.difficultyLevel;
      if (rowData.loadType) subaction.loadType = rowData.loadType;

      const duration = parseNumberValue(rowData.duration);
      if (duration !== undefined) {
        subaction.duration = duration;
      } else if (rowData.duration) {
        subaction.notes = appendNote(
          subaction.notes,
          "Duration",
          rowData.duration
        );
      }
      const repetitions = parseNumberValue(rowData.repetitions);
      if (repetitions !== undefined) {
        subaction.repetitions = repetitions;
      } else if (rowData.repetitions) {
        subaction.notes = appendNote(
          subaction.notes,
          "Reps/Hold",
          rowData.repetitions
        );
      }
      const sets = parseNumberValue(rowData.sets);
      if (sets !== undefined) {
        subaction.sets = sets;
      } else if (rowData.sets) {
        subaction.notes = appendNote(
          subaction.notes,
          "Sets",
          rowData.sets
        );
      }
      const restTime = parseNumberValue(rowData.restTime);
      if (restTime !== undefined) subaction.restTime = restTime;
      const caloriesBurned = parseNumberValue(rowData.caloriesBurned);
      if (caloriesBurned !== undefined)
        subaction.caloriesBurned = caloriesBurned;

      if (rowData.intensityLevel)
        subaction.intensityLevel = rowData.intensityLevel;
      if (rowData.location) subaction.location = rowData.location;
      if (rowData.backgroundImageURL)
        subaction.backgroundImageURL = rowData.backgroundImageURL;
      if (rowData.videoURL) subaction.videoURL = rowData.videoURL;
      if (rowData.quantity) subaction.quantity = rowData.quantity;
      if (rowData.notes) subaction.notes = rowData.notes;
      if (rowData.reflectionPrompt)
        subaction.reflectionPrompt = rowData.reflectionPrompt;
      if (rowData.updatedAt) subaction.updatedAt = rowData.updatedAt;

      const equipmentNeeded = parseListValue(rowData.equipmentNeeded);
      if (equipmentNeeded) subaction.equipmentNeeded = equipmentNeeded;

      const tags = parseListValue(rowData.tags);
      if (tags) subaction.tags = tags;

      const ingredients = parseIngredients(rowData.ingredients);
      if (ingredients) subaction.ingredients = ingredients;

      parsed.push({ subaction });
    });

    return { parsed, errors, warnings };
  };

  const parseCsvToRows = (text: string) => {
    const [headerLine] = text.split(/\r?\n/);
    const delimiter = detectDelimiter(headerLine || "");
    return parseCsv(text, delimiter);
  };

  const parseXlsxToRows = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) as any[][];
  };

  const extractHeadersFromRows = (rows: any[][]) =>
    (rows?.[0] || []).map((header) =>
      String(header ?? "").replace(/^\uFEFF/, "").trim()
    );

  const applyRows = (
    rows: any[][],
    mappingOverride: Record<string, string | null> = aiMapping
  ) => {
    const headers = extractHeadersFromRows(rows);
    setRawHeaders(headers);

    const { parsed, errors, warnings } = parseRowsToSubactions(
      rows,
      mappingOverride,
      defaultActionType
    );
    setParsedSubactions(parsed);
    setParseErrors(errors);
    setParseWarnings(warnings);
  };

  const extractMappingFromResponse = (data: any) => {
    const outputText =
      data?.output_text ||
      (Array.isArray(data?.output)
        ? data.output
            .map((item) =>
              (item?.content || [])
                .map((part) => part?.text || "")
                .join("")
            )
            .join("")
        : "");

    if (outputText) {
      const parsed = JSON.parse(outputText);
      return parsed.mapping || parsed;
    }

    const outputJson =
      data?.output?.[0]?.content?.[0]?.json || data?.output?.[0]?.content?.[0];
    if (outputJson) return outputJson.mapping || outputJson;

    return null;
  };

  const mapHeadersWithAI = async (headers: string[]) => {
    const prompt = `
You map imported file headers to subaction fields.

Rules:
- Only use fields from the allowed list.
- If no good match, return null for that header.
- Prefer exact matches and common synonyms.

Return a JSON object with a single key "mapping", where each key is the original
header string and each value is either a valid field name or null.

Allowed fields:
${TARGET_FIELDS.map((field) => `- ${field}`).join("\n")}

Headers:
${headers.map((header) => `- ${header}`).join("\n")}
`;

    const data = await createOpenAIResponse<any>({
      model: AI_MODEL,
      input: [
        {
          role: "system",
          content: "You are a data import assistant.",
        },
        {
          role: "user",
          content: prompt,
        },
      ],
      text: {
        format: {
          type: "json_object",
        },
      },
    });
    const mapping = extractMappingFromResponse(data);
    if (!mapping) {
      throw new Error("Could not parse AI mapping response.");
    }

    const sanitized: Record<string, string | null> = {};
    Object.entries(mapping as Record<string, any>).forEach(([key, value]) => {
      if (value == null) {
        sanitized[key] = null;
        return;
      }
      sanitized[key] = TARGET_FIELDS.includes(value) ? value : null;
    });

    return sanitized;
  };

  const onFileSelected = async (file: File) => {
    setIsParsing(true);
    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      let rows: any[][] = [];

      if (extension === "csv") {
        const text = await file.text();
        rows = parseCsvToRows(text);
      } else if (extension === "xlsx" || extension === "xls") {
        rows = await parseXlsxToRows(file);
      } else {
        setParseErrors([
          "Unsupported file type. Please upload a .csv, .xlsx, or .xls file.",
        ]);
        setParsedSubactions([]);
        setParseWarnings([]);
        setIsParsing(false);
        return;
      }

      setFileRows(rows);
      const headers = extractHeadersFromRows(rows);
      setRawHeaders(headers);
      applyRows(rows);
      setIsAiMapping(true);
      setAiError(null);
      try {
        const mapping = await mapHeadersWithAI(headers);
        setAiMapping(mapping);
        applyRows(rows, mapping);
      } catch (error) {
        setAiError(
          error instanceof Error
            ? error.message
            : "Failed to map headers with AI."
        );
      } finally {
        setIsAiMapping(false);
      }
      setFileName(file.name);
    } catch (error) {
      setParseErrors(["Failed to read the file."]);
      setParsedSubactions([]);
      setParseWarnings([]);
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = () => {
    if (parsedSubactions.length === 0) {
      setShow(false);
      resetState();
      return;
    }

    const incoming = parsedSubactions.map((item) => item.subaction);
    const existing = standaloneSubactions || [];
    const incomingById = new Map(
      incoming.map((subaction) => [subaction.id, subaction])
    );

    const mergedExisting = existing.map((subaction) =>
      incomingById.has(subaction.id)
        ? incomingById.get(subaction.id)
        : subaction
    );

    const existingIds = new Set(existing.map((subaction) => subaction.id));
    const newOnes = incoming.filter(
      (subaction) =>
        !existingIds.has(subaction.id) &&
        !existingNameSet.has(subaction.name.toLowerCase())
    );

    const nextStandaloneSubactions = [...mergedExisting, ...newOnes];
    setStandaloneSubactions(nextStandaloneSubactions);

    if (habitId) {
      const subactionCollection = firestore.collection(DB_PATH.HABIT_SUBACTION);
      nextStandaloneSubactions.forEach((subaction) => {
        const payload: any = {
          ...subaction,
          habitId,
          communityId,
        };
        delete payload.actionId;
        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined) delete payload[key];
        });
        subactionCollection.doc(subaction.id).set(payload, { merge: true });
      });
    }

    setShow(false);
    resetState();
  };

  const newCount = parsedSubactions.length - duplicateCount;

  return (
    <div>
      <Button
        style={{ marginTop: 8, marginRight: 8 }}
        color="primary"
        variant="outlined"
        onClick={() => setShow(true)}
      >
        Import Subactions
      </Button>
      <Dialog
        positiveAction={handleImport}
        negativeAction={() => {
          setShow(false);
          resetState();
        }}
        onCloseDialog={() => {
          setShow(false);
          resetState();
        }}
        positiveText="Import"
        negativeText="Cancel"
        description=""
        title="Import Subactions"
        isOpen={show}
        ExtraButton={
          <Box style={{ minWidth: 480 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                if (file) onFileSelected(file);
              }}
            />

            {/* Upload zone */}
            <Box
              onClick={() => !isParsing && fileInputRef.current?.click()}
              style={{
                border: "2px dashed #1976d2",
                borderRadius: 8,
                padding: "20px 16px",
                textAlign: "center",
                cursor: isParsing ? "default" : "pointer",
                backgroundColor: "#f5f9ff",
                marginBottom: 16,
                transition: "background-color 0.2s",
              }}
            >
              {isParsing ? (
                <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 10 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Parsing file…
                  </Typography>
                </Box>
              ) : fileName ? (
                <>
                  <Typography variant="body1" style={{ fontWeight: 600 }}>
                    {fileName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Click to choose a different file
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Click to choose a file
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Supports .csv, .xlsx, .xls — required column: <strong>name</strong>
                  </Typography>
                </>
              )}
            </Box>

            {/* Default action type */}
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="default-action-type-label">
                Default Action Type
              </InputLabel>
              <Select
                labelId="default-action-type-label"
                label="Default Action Type"
                value={defaultActionType}
                onChange={(event) => {
                  const nextType = event.target.value as ActionType;
                  setDefaultActionType(nextType);
                  if (fileRows.length > 0) {
                    applyRows(fileRows, aiMapping);
                  }
                }}
              >
                {Object.values(ActionType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4, marginBottom: 12 }}>
              Applied to rows that don't include an <code>actionType</code> column.
            </Typography>

            {/* AI mapping status */}
            {isAiMapping && (
              <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="textSecondary">
                  Mapping headers with AI…
                </Typography>
              </Box>
            )}
            {!isAiMapping && Object.keys(aiMapping).length > 0 && (
              <Box mb={1}>
                <Chip
                  size="small"
                  label={`AI mapped ${Object.values(aiMapping).filter(Boolean).length} / ${rawHeaders.length} headers`}
                  style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
                />
              </Box>
            )}
            {aiError && (
              <Typography variant="caption" color="error" style={{ display: "block", marginBottom: 8 }}>
                {aiError}
              </Typography>
            )}

            {/* Results section */}
            {parsedSubactions.length > 0 && (
              <>
                <Divider style={{ margin: "12px 0" }} />

                {/* Stats chips */}
                <Box display="flex" style={{ gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  <Chip
                    size="small"
                    label={`${newCount} new`}
                    style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                  />
                  {duplicateCount > 0 && (
                    <Chip
                      size="small"
                      label={`${duplicateCount} already exist — will skip`}
                      style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
                    />
                  )}
                  {parseErrors.length > 0 && (
                    <Chip
                      size="small"
                      label={`${parseErrors.length} parse error${parseErrors.length > 1 ? "s" : ""}`}
                      style={{ backgroundColor: "#ffebee", color: "#c62828" }}
                    />
                  )}
                </Box>

                {/* Parse errors */}
                {parseErrors.length > 0 && (
                  <Box mt={1} style={{ backgroundColor: "#ffebee", borderRadius: 6, padding: "8px 12px" }}>
                    {parseErrors.slice(0, 5).map((error, index) => (
                      <Typography key={`${error}-${index}`} variant="caption" style={{ display: "block", color: "#c62828" }}>
                        {error}
                      </Typography>
                    ))}
                    {parseErrors.length > 5 && (
                      <Typography variant="caption" style={{ color: "#c62828" }}>
                        …and {parseErrors.length - 5} more
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}

            {/* Collapsible columns reference */}
            <Divider style={{ margin: "12px 0" }} />
            <Box
              onClick={() => setShowColumns((v) => !v)}
              style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                Supported columns
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {showColumns ? "Hide" : "Show"}
              </Typography>
            </Box>
            <Collapse in={showColumns}>
              <Box mt={1} style={{ backgroundColor: "#f5f5f5", borderRadius: 6, padding: "8px 12px" }}>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 4 }}>
                  <strong>Required:</strong> name
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <strong>Optional:</strong> description, actionType, duration, repetitions, sets, restTime,
                  caloriesBurned, intensityLevel, equipmentNeeded, location, backgroundImageURL, videoURL,
                  quantity, notes, instructionsURL, tags, ingredients, createdAt, updatedAt, id, videoFileName,
                  reflectionPrompt (subaction assistant chat prompt), phase, primaryIntent, secondaryBenefits, bodyRegions, movementQualities,
                  compensationsAddressed, userUseCases, difficultyLevel, loadType.
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                  List fields use <code>|</code> or <code>;</code> separators. Ingredients accepts a JSON array.
                </Typography>
              </Box>
            </Collapse>
          </Box>
        }
      />
    </div>
  );
};

export default ImportSubactions;
